import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import {
  BubbleAccordion,
  BubbleButton,
  BubbleDebounce,
  BubbleCheckbox,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleTypeahead,
  BubbleTable,
  BubbleBadge,
} from '@go-client/components/BubbleUI';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import './GroupsHome.scss';

import { useUserInfo } from '@go-client/context/UserInfoContext';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import GroupsNavigation from '@go-client/components/Navigations/GroupsNavigation';
import PageWrapper from './PageWrapper';
import Page from './Page';
import { MenuBar } from '@go-client/components';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { groupResourceClient } from '@go-client/lib/api/v2/groupResource';
import GroupResourceForm, {
  GroupsResourceMode,
} from '@go-client/components/GroupBookingForm/GroupResourceForm';
import {
  BubbleTypeaheadOption,
  BubbleTypeaheadValue,
} from '@go-client/components/BubbleUI/BubbleTypeahead';
import uuid from 'uuid';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { useGroupResourceData } from '@go-client/hooks/useGroupResourceData';
import { canCreateOrEditGroupResources } from '@go-shared/lib/permissions';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { hashStringToColor } from '@go-shared/lib/utils';
import {
  GroupResource,
  GroupResourcePaginationResult,
  GroupResourcePaginationResultSchema,
  PaginateGroupResourcesParams,
} from '@go-shared/types/zod/groupResource';
import { getLocaleDateTimeStringUTC } from '@go-shared/lib/time';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';
import { PaginationMetadata } from '@go-shared/types/zod/pagination';
import { DEFAULT_PER_PAGE } from '@go-shared/types/pagination';
import { safeFetch } from '@go-client/lib/api';
import { exportGroupResouceCSV } from '@go-client/lib/api/groupResource';

type ResourceTableItem = Omit<GroupResource, '_id'> & {
  id: string;
  color: string;
};

interface GroupsFilters {
  selectedTypes: Array<BubbleTypeaheadValue<string>>;
  updatedAt: { startDate?: string; endDate?: string };
  createdAt: { startDate?: string; endDate?: string };
  users: Array<BubbleTypeaheadValue<string>>;
}

function makeBadgeFromType(resource: ResourceTableItem) {
  const { resourceType, color } = resource;

  return <BubbleBadge color={color}>{resourceType}</BubbleBadge>;
}

function renderDate(fullDate: Date) {
  let element = <></>;

  if (fullDate) {
    const updatedAt = getLocaleDateTimeStringUTC(fullDate);
    const [date, time] = updatedAt.split(', ');
    element = (
      <>
        <div className="block whitespace-nowrap">{date}</div>
        <div className="block whitespace-nowrap">{time}</div>
      </>
    );
  }

  return element;
}

type GroupResourceTableItem = GroupResourcePaginationResult & {
  data: (GroupResourcePaginationResult['data'][number] & {
    key: string;
    refKey: string;
    item: string;
    id: string;
    color: string;
  })[];
};

export default function ResourceLibrary(props: RouteComponentProps) {
  const { activeProperty } = useActiveProperty();

  const { user } = useUserInfo();
  const serverInfo = useServerInfo();
  const [showArchive, setShowArchive] = useState<{ [key: string]: boolean }>({});
  const [mode, setMode] = useState<GroupsResourceMode>({ type: 'default' });
  const [filters, setFilters] = useState<GroupsFilters>({
    selectedTypes: [],
    users: [],
    updatedAt: { startDate: '', endDate: '' },
    createdAt: { startDate: '', endDate: '' },
  });
  const [resourceType, setResourceType] = useState<Array<BubbleTypeaheadOption<string>>>([]);
  const [users, setUsers] = useState<Array<BubbleTypeaheadOption<string>>>([]);
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );
  const [paginationParams, setPaginationParams] = useState<PaginateGroupResourcesParams>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: 'createdAt',
    userIds: [],
    type: [],
    archived: false,
  });
  const [dataBundle, setDataBundle] = useState<GroupResourceTableItem>();
  const [isBusy, setIsBusy] = useState(false);

  const { resourceData, loading } = useGroupResourceData(serverInfo.brand._id);

  async function refreshDataBundle() {
    await fetchDataBundle();
  }

  useEffect(() => {
    refreshDataBundle();
  }, [paginationParams]);

  useEffect(() => {
    const resourceTypeOptions = [
      ...resourceData.resourceTypes,
      { name: 'unassigned', _id: uuid() },
    ].map(s => ({
      key: s.name,
      item: s.name,
    }));

    const uniqueUsers = resourceData.users.map(user => ({
      key: user._id,
      refKey: user._id,
      item: `${user.lastName}, ${user.firstName}`,
    }));

    setUsers(uniqueUsers);
    setResourceType(resourceTypeOptions);
    const resourceTypeMap = mapBy(resourceTypeOptions, 'key');

    setFilters({
      ...filters,
      selectedTypes: resourceTypeOptions
        .map(sopt => {
          const refOpt = resourceTypeMap[sopt.key];
          return refOpt
            ? {
                key: uuid(),
                item: sopt.item,
                refKey: refOpt.key,
              }
            : undefined;
        })
        .filter(notUndefined),
    });
  }, [resourceData.resourceTypes]);

  const tableFields: Array<BubbleTableFields<ResourceTableItem>> = [
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'type',
      label: 'Type',
    },
    {
      key: 'price',
      label: 'Price',
      behavior: BubbleTableFieldBehavior.NoWrap,
    },
    { key: 'updatedBy', label: 'Updated By', thProps: { className: 'whitespace-nowrap' } },
    { key: 'updatedAt', label: 'Updated At', thProps: { className: 'whitespace-nowrap' } },
    { key: 'createdBy', label: 'Created By', thProps: { className: 'whitespace-nowrap' } },
    { key: 'createdAt', label: 'Created At', thProps: { className: 'whitespace-nowrap' } },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<ResourceTableItem>> = [
    {
      key: 'name',
      render: item => <> {item.name}</>,
    },
    {
      key: 'type',
      render: item => makeBadgeFromType(item),
    },
    {
      key: 'price',
      render: item => <>${item.price}</>,
    },
    {
      key: 'createdBy',
      render: item => (
        <>
          {item.createdBy.firstName} {item.createdBy.lastName}
        </>
      ),
    },
    {
      key: 'updatedBy',
      render: item => (
        <>
          {item.updatedBy.firstName} {item.updatedBy.lastName}
        </>
      ),
    },
    {
      key: 'updatedAt',
      render: item => renderDate(item.updatedAt),
    },
    {
      key: 'createdAt',
      render: item => renderDate(item.createdAt),
    },
    {
      key: 'actions',
      render: item =>
        canCreateOrEditGroupResources(user, serverInfo) && (
          <>
            <BubbleButton
              onClick={() => handleEdit(item)}
              className="p-2-i"
              variant="navy"
              inverse
              disabled={false}
              icon="edit"
            />
            <BubbleButton
              onClick={() => handleClone(item)}
              className="p-2-i"
              variant="slate"
              inverse
              disabled={false}
              icon="copy"
            />
            {!showArchive[item.id] && (
              <BubbleButton
                className="p-2-i"
                variant="dirt"
                inverse
                onClick={() => {
                  setShowArchive({ ...showArchive, [item.id]: true });
                }}
                icon="trash-alt"
              />
            )}
            {showArchive[item.id] && (
              <>
                <BubbleButton
                  className="p-2-i"
                  variant="crimson"
                  inverse
                  onClick={() => handleArchive(item)}
                  icon="trash-check"
                />
                <BubbleButton
                  className="p-2-i"
                  variant="dirt"
                  inverse
                  onClick={() => {
                    setShowArchive({ ...showArchive, [item.id]: false });
                  }}
                  icon="trash-xmark"
                />
              </>
            )}
          </>
        ),
    },
  ];

  async function fetchDataBundle() {
    const res = await safeFetch(
      groupResourceClient.paginate({
        query: {
          ...paginationParams,
          archived: String(paginationParams.archived),
        },
      }),
      GroupResourcePaginationResultSchema
    );
    setPaginationMetadata(res.meta);

    const data = res.data.map(gr => ({
      ...gr,
      key: gr._id,
      refKey: gr._id,
      item: gr.name,
      id: gr._id,
      color: hashStringToColor(gr.resourceType),
    }));
    setDataBundle({ ...res, data });
  }

  async function handleArchive(item: ResourceTableItem) {
    await groupResourceClient.archive({
      params: { resourceId: item.id },
      body: { archived: !item.archived },
    });
    refreshDataBundle();
  }

  function handleEdit(item: {
    id: string;
    name: string;
    price: number;
    resourceType: string;
    description: string;
  }): void {
    setMode({ type: 'edit', data: item });
  }

  function handleClone(item: {
    id: string;
    name: string;
    price: number;
    resourceType: string;
    description: string;
  }): void {
    setMode({ type: 'clone', data: item });
  }

  function handleCloseGroupResourceForm() {
    setMode({ type: 'default' });
  }

  async function handleExportGroupResources() {
    setIsBusy(true);
    try {
      await exportGroupResouceCSV();
    } finally {
      setIsBusy(false);
    }
  }
  return (
    <>
      <PageWrapper {...props}>
        <Page className="Groups absolute flex items-stretch">
          <MenuBar {...props} property={activeProperty} />
          <div className="SideMenu bg-forest-darkest flex flex-col">
            <div className="bg-white flex flex-col items-center">
              <BrandLogo logo={activeProperty.brand.logo} />
              <PropertyBanner {...props} property={activeProperty} swappable />
            </div>
            <div className={`flex flex-col relative items-stretch p-4 bg-forest-darker`}>
              <div className="grid gap-2">
                {canCreateOrEditGroupResources(user, serverInfo) && (
                  <BubbleButton
                    variant="lime"
                    className="text-left"
                    block
                    icon="plus"
                    onClick={() => {
                      setMode({ type: 'create' });
                    }}
                  >
                    New Resource
                  </BubbleButton>
                )}

                <div className="w-full flex items-stretch">
                  <BubbleOverlay loading={isBusy} loadingClassName="rounded" className="flex-grow">
                    <BubbleButton
                      variant="slate"
                      className="text-left"
                      block
                      disabled={isBusy}
                      onClick={handleExportGroupResources}
                      icon="file-export"
                      style={{ fontSize: '12px', height: '36px' }}
                    >
                      Export Group Resources
                    </BubbleButton>
                  </BubbleOverlay>
                </div>
              </div>
            </div>
            <BubbleAccordion
              variant="forest"
              className="flex-grow flex flex-col border-t border-forest-darkest"
              labels={['Filters']}
              open={[0]}
              locked={[0]}
            >
              <div className="grid gap-4 p-4">
                <div className="dropdown-inline-fix top-0 z-10 bg-forest-darkest">
                  <BubbleFormGroup label="Resource Types" labelClassName="text-white-i">
                    <BubbleTypeahead
                      block
                      variant="forest"
                      placeholder="Filter types shown..."
                      menuClassName="max-h-80 overflow-y-auto"
                      value={filters.selectedTypes}
                      options={resourceType}
                      onAdd={v => {
                        setFilters({
                          ...filters,
                          selectedTypes: [...filters.selectedTypes, v],
                        });
                        setPaginationParams({
                          ...paginationParams,
                          page: 1,
                          type: [...filters.selectedTypes.map(v => v.refKey), v.refKey],
                        });
                      }}
                      onRemove={v => {
                        setFilters({
                          ...filters,
                          selectedTypes: filters.selectedTypes.filter(v2 => v2.key !== v.key),
                        });
                        setPaginationParams({
                          ...paginationParams,
                          page: 1,
                          type: filters.selectedTypes.flatMap(v2 =>
                            v2.key !== v.key ? [v2.refKey] : []
                          ),
                        });
                      }}
                      onClearAll={() => {
                        setPaginationParams({ ...paginationParams, page: 1, type: [] });
                        setFilters({ ...filters, selectedTypes: [] });
                      }}
                    />
                  </BubbleFormGroup>
                </div>
                <BubbleFormGroup label="Updated At" labelClassName="text-white-i">
                  <div className="grid grid-cols-1 gap-1">
                    <BubbleDebounce
                      onTrigger={[
                        (date: string) => {
                          setPaginationParams({
                            ...paginationParams,
                            page: 1,
                            updatedAtStart: date,
                          });
                        },
                        500,
                      ]}
                    >
                      {({ debounce }) => (
                        <BubbleInput
                          variant="forest"
                          type="date"
                          value={paginationParams?.updatedAtStart}
                          onValue={debounce}
                        />
                      )}
                    </BubbleDebounce>
                    <BubbleDebounce
                      onTrigger={[
                        (date: string) => {
                          setPaginationParams({
                            ...paginationParams,
                            page: 1,
                            updatedAtEnd: date,
                          });
                        },
                        500,
                      ]}
                    >
                      {({ debounce }) => (
                        <BubbleInput
                          variant="forest"
                          type="date"
                          value={paginationParams.updatedAtEnd}
                          onValue={debounce}
                        />
                      )}
                    </BubbleDebounce>
                  </div>
                </BubbleFormGroup>
                <BubbleFormGroup label="Created At" labelClassName="text-white-i">
                  <div className="grid grid-cols-1 gap-1">
                    <BubbleDebounce
                      onTrigger={[
                        (date: string) => {
                          setPaginationParams({
                            ...paginationParams,
                            page: 1,
                            createdAtStart: date,
                          });
                        },
                        500,
                      ]}
                    >
                      {({ debounce }) => (
                        <BubbleInput
                          variant="forest"
                          type="date"
                          value={paginationParams.createdAtStart}
                          onValue={debounce}
                        />
                      )}
                    </BubbleDebounce>
                    <BubbleDebounce
                      onTrigger={[
                        (date: string) => {
                          setPaginationParams({
                            ...paginationParams,
                            page: 1,
                            createdAtEnd: date,
                          });
                        },
                        500,
                      ]}
                    >
                      {({ debounce }) => (
                        <BubbleInput
                          variant="forest"
                          type="date"
                          value={paginationParams.createdAtEnd}
                          onValue={debounce}
                        />
                      )}
                    </BubbleDebounce>
                  </div>
                </BubbleFormGroup>

                <BubbleFormGroup label="Users" labelClassName="text-white-i">
                  <BubbleTypeahead
                    block
                    variant="forest"
                    placeholder="Filter users"
                    menuClassName="max-h-80 overflow-y-auto"
                    value={filters.users}
                    options={users}
                    labelExtractor={l => l}
                    onAdd={v => {
                      setFilters({
                        ...filters,
                        users: [...filters.users, v],
                      });
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        userIds: [...(paginationParams.userIds ?? []), v.refKey],
                      });
                    }}
                    onRemove={v => {
                      setFilters({
                        ...filters,
                        users: filters.users.filter(v2 => v2.key !== v.key),
                      });
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        userIds: (paginationParams.userIds ?? []).filter(v2 => v2 !== v.refKey),
                      });
                    }}
                    onClearAll={() => {
                      setFilters({ ...filters, users: [] });
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        userIds: [],
                      });
                    }}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup labelClassName="text-white-i">
                  <BubbleCheckbox
                    variant="forest"
                    className="text-white"
                    checked={paginationParams.archived}
                    onValue={checked => {
                      setPaginationParams({ ...paginationParams, page: 1, archived: checked });
                    }}
                    label="Show Only Archived"
                  />
                </BubbleFormGroup>
              </div>
            </BubbleAccordion>
          </div>
          <div className="GroupsContent bg-blue-lightest overflow-y-auto flex-grow">
            <div className="GroupsHeader align-with-logo flex items-end px-5 sticky top-0 left-0 z-10 bg-blue-lightest">
              <div className="mb-3 w-full flex items-end px-3">
                <GroupsNavigation active="resources"></GroupsNavigation>
              </div>
            </div>

            <div className="GroupsBody mx-5 content-minus-logo min-w-900">
              <BubbleOverlay loading={loading}>
                <BubbleTable
                  responsive
                  striped
                  headVariant="forest"
                  fields={tableFields}
                  items={dataBundle?.data}
                  stickyHeader={130}
                  templates={tableTemplates}
                  showPretitle
                  pretitleClassName="p-0-i"
                  onRowClick={item => {
                    if (canCreateOrEditGroupResources(user, serverInfo)) handleEdit(item);
                  }}
                  pretitleRender={() => (
                    <div className="px-4 pt-4 pb-1 flex items-center">
                      <BubbleDebounce
                        onTrigger={[
                          (search: string) =>
                            setPaginationParams({ ...paginationParams, page: 1, search }),
                          500,
                        ]}
                      >
                        {({ debounce }) => (
                          <div className="flex-grow">
                            <BubbleInput
                              variant="forest"
                              inputClassName="outline-none-i"
                              type="text"
                              block
                              value={paginationParams.search}
                              onValue={debounce}
                              placeholder="Search..."
                            />
                          </div>
                        )}
                      </BubbleDebounce>
                    </div>
                  )}
                />
                {paginationMetadata && (
                  <BubbleTablePaginationFooter
                    paginationMetadata={paginationMetadata}
                    perPage={paginationParams.perPage}
                    onLoadMoreClick={() =>
                      setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
                    }
                    onPerPageChange={value =>
                      setPaginationParams({ ...paginationParams, page: 1, perPage: value })
                    }
                  />
                )}
              </BubbleOverlay>
              <div className="mt-3">&nbsp;</div>
            </div>
          </div>
          {mode.type !== 'default' && (
            <BubbleDrawer hideTools containerClassName="bg-eggshell">
              <GroupResourceForm
                types={resourceData.resourceTypes.map(r => r.name)}
                mode={mode}
                onClose={handleCloseGroupResourceForm}
                onSubmitGroupResource={refreshDataBundle}
              />
            </BubbleDrawer>
          )}
        </Page>
      </PageWrapper>
    </>
  );
}
